/**
 * Featured Product Image Viewer
 * Handles opening and closing of image viewer modal for featured product cards
 */

class FeaturedProductImageViewer {
  constructor() {
    this.currentViewer = null;
    this.currentImageIndex = 0;
    this.images = [];
    this.init();
  }

  init() {
    // Add click listeners to all featured product card images with viewer enabled
    document.addEventListener("click", (event) => {
      const imageElement = event.target.closest(
        ".featured-product-card__image--viewer img"
      );
      if (imageElement) {
        event.preventDefault();
        this.openViewer(imageElement);
      }
    });

    // Add touch support for mobile
    let touchStartX = 0;
    let touchStartY = 0;

    document.addEventListener("touchstart", (event) => {
      const imageElement = event.target.closest(
        ".featured-product-card__image--viewer img"
      );
      if (imageElement) {
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      }
    });

    document.addEventListener("touchend", (event) => {
      const imageElement = event.target.closest(
        ".featured-product-card__image--viewer img"
      );
      if (imageElement) {
        const touchEndX = event.changedTouches[0].clientX;
        const touchEndY = event.changedTouches[0].clientY;
        const deltaX = Math.abs(touchEndX - touchStartX);
        const deltaY = Math.abs(touchEndY - touchStartY);

        // Only trigger if it's a tap (not a swipe)
        if (deltaX < 10 && deltaY < 10) {
          event.preventDefault();
          this.openViewer(imageElement);
        }
      }
    });

    // Add click listeners to close buttons
    document.addEventListener("click", (event) => {
      if (event.target.closest(".featured-product-image-viewer__close")) {
        this.closeViewer();
      }
    });

    // Add click listeners to thumbnails
    document.addEventListener("click", (event) => {
      const thumbnail = event.target.closest(
        ".featured-product-image-viewer__thumbnail"
      );
      if (thumbnail) {
        const imageIndex = parseInt(thumbnail.dataset.imageIndex);
        this.showImage(imageIndex);
      }
    });

    // Close viewer when clicking on overlay
    document.addEventListener("click", (event) => {
      if (
        event.target.classList.contains(
          "featured-product-image-viewer__overlay"
        )
      ) {
        this.closeViewer();
      }
    });

    // Handle keyboard events
    document.addEventListener("keydown", (event) => {
      if (this.currentViewer && this.currentViewer.style.display !== "none") {
        if (event.key === "Escape") {
          this.closeViewer();
        } else if (event.key === "ArrowLeft") {
          this.previousImage();
        } else if (event.key === "ArrowRight") {
          this.nextImage();
        }
      }
    });

    // Add swipe gesture support for mobile image navigation
    let swipeStartX = 0;
    let swipeStartY = 0;

    document.addEventListener("touchstart", (event) => {
      if (this.currentViewer && this.currentViewer.style.display !== "none") {
        swipeStartX = event.touches[0].clientX;
        swipeStartY = event.touches[0].clientY;
      }
    });

    document.addEventListener("touchend", (event) => {
      if (this.currentViewer && this.currentViewer.style.display !== "none") {
        const swipeEndX = event.changedTouches[0].clientX;
        const swipeEndY = event.changedTouches[0].clientY;
        const deltaX = swipeEndX - swipeStartX;
        const deltaY = swipeEndY - swipeStartY;

        // Check if it's a horizontal swipe (more horizontal than vertical movement)
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
          if (deltaX > 0) {
            // Swipe right - previous image
            this.previousImage();
          } else {
            // Swipe left - next image
            this.nextImage();
          }
        }
      }
    });
  }

  openViewer(clickedImage) {
    const productId =
      clickedImage.closest("[data-product-id]").dataset.productId;
    const imageIndex = parseInt(clickedImage.dataset.imageIndex);

    this.currentViewer = document.getElementById(`image-viewer-${productId}`);
    if (!this.currentViewer) return;

    // Get all images for this product
    this.images = Array.from(
      clickedImage.parentElement.querySelectorAll("img")
    );
    this.currentImageIndex = imageIndex;

    // Show the viewer
    this.currentViewer.style.display = "block";
    document.body.classList.add("image-viewer-open");

    // Load the clicked image
    this.showImage(imageIndex);

    // Add fade-in animation
    requestAnimationFrame(() => {
      this.currentViewer.style.opacity = "0";
      this.currentViewer.style.transition = "opacity 0.3s ease";
      requestAnimationFrame(() => {
        this.currentViewer.style.opacity = "1";
      });
    });
  }

  closeViewer() {
    if (!this.currentViewer) return;

    // Add fade-out animation
    this.currentViewer.style.transition = "opacity 0.3s ease";
    this.currentViewer.style.opacity = "0";

    setTimeout(() => {
      this.currentViewer.style.display = "none";
      this.currentViewer.style.opacity = "1";
      this.currentViewer.style.transition = "";
      document.body.classList.remove("image-viewer-open");
      this.currentViewer = null;
    }, 300);
  }

  showImage(imageIndex) {
    if (
      !this.currentViewer ||
      imageIndex < 0 ||
      imageIndex >= this.images.length
    )
      return;

    this.currentImageIndex = imageIndex;
    const image = this.images[imageIndex];
    const viewerImage = this.currentViewer.querySelector(
      ".featured-product-image-viewer__image"
    );
    const thumbnails = this.currentViewer.querySelectorAll(
      ".featured-product-image-viewer__thumbnail"
    );

    // Update main image
    viewerImage.src = image.dataset.imageSrc;
    viewerImage.alt = image.alt;

    // Update active thumbnail
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle("active", index === imageIndex);
    });
  }

  previousImage() {
    const newIndex =
      this.currentImageIndex > 0
        ? this.currentImageIndex - 1
        : this.images.length - 1;
    this.showImage(newIndex);
  }

  nextImage() {
    const newIndex =
      this.currentImageIndex < this.images.length - 1
        ? this.currentImageIndex + 1
        : 0;
    this.showImage(newIndex);
  }
}

// Initialize the image viewer when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new FeaturedProductImageViewer();
});

// Also initialize if DOM is already loaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new FeaturedProductImageViewer();
  });
} else {
  new FeaturedProductImageViewer();
}
